import { useCallback, useEffect, useRef, useState } from 'react';
import axiosInstance from '../redux/api';

/*
 * Loads one order, by id, into local state.
 *
 * Replaces `dispatch(getSingleOrderView(id))` + a read of the global
 * `reusableSlice.OrderListView`, which caused two defects:
 *
 * 1. Stale order painted on navigation. The detail page never checked that the
 *    order in the store was the one the URL asked for, and OrderPreviewDrawer
 *    writes to the same key. Going from /orderview/72 to /orderview/73 rendered
 *    all of order 72 — customer, address, items, totals, refund button — until
 *    the new fetch resolved. Detail data is route-scoped, so it lives here now,
 *    and `status` only becomes 'ready' once the payload's own order_id matches
 *    the requested one.
 *
 * 2. A failed fetch spun forever. The thunk swallowed errors into
 *    `setOrderListView([])`, which the page read as "still loading". Errors are
 *    now a first-class state with a retry.
 *
 * A monotonic request id guards against out-of-order responses: fire a reload,
 * navigate, and the older in-flight response can't overwrite the newer one.
 */

export const ORDER_STATE = {
  LOADING: 'loading',
  ERROR: 'error',
  READY: 'ready',
};

const NOT_FOUND_MESSAGE = 'This order could not be found.';
const GENERIC_MESSAGE = 'Could not load this order.';

const readError = (error) => {
  if (error?.response?.status === 404) return NOT_FOUND_MESSAGE;
  return (
    error?.response?.data?.message ||
    error?.response?.data?.response?.message ||
    GENERIC_MESSAGE
  );
};

export const useOrder = (orderId) => {
  const [state, setState] = useState({
    status: ORDER_STATE.LOADING,
    order: null,
    error: null,
  });

  // Bumped on every request; only the newest one is allowed to write state.
  const requestRef = useRef(0);
  // Lets an unmount (or an id change) silence responses still in flight.
  const activeIdRef = useRef(orderId);

  useEffect(() => {
    activeIdRef.current = orderId;
  }, [orderId]);

  const load = useCallback(
    async ({ quiet = false } = {}) => {
      if (!orderId) return;
      const requestId = ++requestRef.current;

      if (!quiet) {
        setState((prev) => ({
          status: ORDER_STATE.LOADING,
          order: prev.order,
          error: null,
        }));
      }

      try {
        const response = await axiosInstance.get(`admin/orders/${orderId}`);
        if (requestId !== requestRef.current) return;
        if (String(activeIdRef.current) !== String(orderId)) return;

        const payload = response?.data?.response;
        const isOrder =
          payload &&
          typeof payload === 'object' &&
          !Array.isArray(payload) &&
          payload.order_id != null;

        // A well-formed 200 that isn't this order is a miss, not a success —
        // rendering it would reintroduce exactly the bug this hook exists to fix.
        if (!isOrder || String(payload.order_id) !== String(orderId)) {
          setState({
            status: ORDER_STATE.ERROR,
            order: null,
            error: NOT_FOUND_MESSAGE,
          });
          return;
        }

        setState({ status: ORDER_STATE.READY, order: payload, error: null });
      } catch (error) {
        if (requestId !== requestRef.current) return;
        if (String(activeIdRef.current) !== String(orderId)) return;
        setState({
          status: ORDER_STATE.ERROR,
          order: null,
          error: readError(error),
        });
      }
    },
    [orderId],
  );

  useEffect(() => {
    // Drop whatever the previous route left behind before the next fetch, so a
    // slow response can never be mistaken for the new order's data.
    setState({ status: ORDER_STATE.LOADING, order: null, error: null });
    load();
  }, [load]);

  useEffect(
    () => () => {
      // Invalidate in-flight responses on unmount.
      requestRef.current += 1;
    },
    [],
  );

  /**
   * Applies an optimistic change without a round trip. Accepts a patch object
   * or an updater. Ignored unless an order is actually loaded, so a mutation
   * racing a navigation can't resurrect a dead record.
   */
  const patchLocal = useCallback((patch) => {
    setState((prev) => {
      if (!prev.order) return prev;
      const next =
        typeof patch === 'function'
          ? patch(prev.order)
          : { ...prev.order, ...patch };
      return { ...prev, order: next };
    });
  }, []);

  return {
    order: state.order,
    error: state.error,
    isLoading: state.status === ORDER_STATE.LOADING,
    isError: state.status === ORDER_STATE.ERROR,
    isReady: state.status === ORDER_STATE.READY,
    reload: load,
    patchLocal,
  };
};

export default useOrder;
